/* global wl */
import { Fragment, useEffect, useState } from "@wordpress/element";
import {
    Alert,
    Card,
    Space,
    Table,
    Tabs,
    Tag,
    Empty,
    Collapse,
    Typography,
    Spin,
} from "antd";

const { Panel } = Collapse;
import { withGetScreen } from "react-getscreen";

import Guy_Table, { MlbTeamMobileCard } from "../components/guy-table";
import AchievementBadges from "../components/beer-achievement";
import { useBlockConfig } from "../state";
import { formatMlbGameTimeCentral } from "../utils/mlb-display";

const { TabPane } = Tabs;
const { Text, Paragraph } = Typography;

/** Defaults mirror PHP `Wins_League_ACF_Blocks::get_mlb_block_config` and `frontend.js`. */
const MLB_BLOCK_DEFAULTS = {
    show_standings: true,
    show_draft: true,
    show_schedule: false,
    show_insights: false,
    show_live_strip: false,
    show_recaps: false,
};

const getExtras = () =>
    typeof wl !== "undefined" && wl.mlb_extras && typeof wl.mlb_extras === "object"
        ? wl.mlb_extras
        : {};

const formatStart = (iso) => formatMlbGameTimeCentral(iso);

const inningLabel = (row) => {
    if (row.inning == null) {
        return "—";
    }
    const half =
        row.inning_half === "T"
            ? "Top"
            : row.inning_half === "B"
            ? "Bot"
            : "";
    return half ? `${half} ${row.inning}` : String(row.inning);
};

const formatCount = (row) => {
    const b = row.balls;
    const s = row.strikes;
    const o = row.outs;
    if (b == null && s == null && o == null) {
        return null;
    }
    const parts = [];
    if (b != null || s != null) {
        parts.push(`${b ?? "–"}-${s ?? "–"} count`);
    }
    if (o != null) {
        parts.push(`${o} out${o === 1 ? "" : "s"}`);
    }
    return parts.join(" · ");
};

const formatRunners = (row) => {
    const bits = [];
    if (row.runner_on_first) {
        bits.push("1st");
    }
    if (row.runner_on_second) {
        bits.push("2nd");
    }
    if (row.runner_on_third) {
        bits.push("3rd");
    }
    if (!bits.length) {
        return "Bases empty";
    }
    return `Runners: ${bits.join(", ")}`;
};

const LineScoreTable = ({ box, row }) => {
    const innings = Array.isArray(box.innings) ? box.innings : [];
    if (!innings.length) {
        return null;
    }
    const nums = innings.map((i) => i.inning).filter((n) => n != null);
    const maxInn = Math.max(9, ...nums);
    const cols = [];
    for (let n = 1; n <= maxInn; n++) {
        cols.push(n);
    }
    const getCell = (inningNum, side, key) => {
        const inn = innings.find((x) => x.inning === inningNum);
        if (!inn) {
            return "—";
        }
        const v = inn[`${side}_${key}`];
        return v == null ? "—" : v;
    };
    const awayTeam = box.away_team || row.away_team;
    const homeTeam = box.home_team || row.home_team;
    const ta = box.totals?.away || {};
    const th = box.totals?.home || {};
    return (
        <div className="wl-mlb-linescore">
            <Text type="secondary">Line score (by inning)</Text>
            <table>
                <thead>
                    <tr>
                        <th className="wl-mlb-team-cell" scope="col"></th>
                        {cols.map((n) => (
                            <th key={n} scope="col">
                                {n}
                            </th>
                        ))}
                        <th scope="col">R</th>
                        <th scope="col">H</th>
                        <th scope="col">E</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <th className="wl-mlb-team-cell" scope="row">
                            {awayTeam}
                        </th>
                        {cols.map((n) => (
                            <td key={n}>{getCell(n, "away", "r")}</td>
                        ))}
                        <td>{ta.r ?? "—"}</td>
                        <td>{ta.h ?? "—"}</td>
                        <td>{ta.e ?? "—"}</td>
                    </tr>
                    <tr>
                        <th className="wl-mlb-team-cell" scope="row">
                            {homeTeam}
                        </th>
                        {cols.map((n) => (
                            <td key={n}>{getCell(n, "home", "r")}</td>
                        ))}
                        <td>{th.r ?? "—"}</td>
                        <td>{th.h ?? "—"}</td>
                        <td>{th.e ?? "—"}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

const BattingMiniTable = ({ title, players }) => {
    if (!Array.isArray(players) || !players.length) {
        return null;
    }
    const cols = [
        { title: "Hitter", dataIndex: "name", key: "name", ellipsis: true },
        { title: "AB", dataIndex: "ab", key: "ab", width: 44, align: "center" },
        { title: "H", dataIndex: "h", key: "h", width: 36, align: "center" },
        { title: "R", dataIndex: "r", key: "r", width: 36, align: "center" },
        { title: "RBI", dataIndex: "rbi", key: "rbi", width: 44, align: "center" },
        { title: "HR", dataIndex: "hr", key: "hr", width: 40, align: "center" },
    ];
    const data = players.map((p, i) => ({ ...p, key: `b-${i}-${p.name}` }));
    return (
        <div className="wl-mlb-batting-mini">
            <Text strong>{title}</Text>
            <Table
                size="small"
                pagination={false}
                columns={cols}
                dataSource={data}
                rowKey="key"
            />
        </div>
    );
};

const GameRecap = ({ row }) => {
    const [box, setBox] = useState(null);
    const [loading, setLoading] = useState(!!row.game_id);
    const [err, setErr] = useState(false);

    useEffect(() => {
        if (!row.game_id) {
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        const root =
            typeof wl !== "undefined" && wl.root
                ? wl.root
                : `${window.location.origin}/wp-json/`;
        const url = `${root}wins-league/v1/mlb-boxscore/${row.game_id}`;
        fetch(url, {
            credentials: "same-origin",
            cache: "no-store",
            headers:
                typeof wl !== "undefined" && wl.nonce
                    ? { "X-WP-Nonce": wl.nonce }
                    : {},
        })
            .then((res) => {
                if (!res.ok) {
                    throw new Error("box");
                }
                return res.json();
            })
            .then((data) => {
                if (!cancelled) {
                    setBox(data);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setErr(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [row.game_id]);

    const dec = box?.decisions || {};
    const decBits = [];
    if (dec.win) {
        decBits.push(`W: ${dec.win}`);
    }
    if (dec.loss) {
        decBits.push(`L: ${dec.loss}`);
    }
    if (dec.save) {
        decBits.push(`SV: ${dec.save}`);
    }

    return (
        <div className="wl-mlb-recap">
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                {row.channel ? (
                    <div>
                        <Text type="secondary">TV </Text>
                        {row.channel}
                    </div>
                ) : null}
                {row.away_starter || row.home_starter ? (
                    <div>
                        <Text type="secondary">Starting pitchers </Text>
                        {(row.away_starter || "—") + " @ " + (row.home_starter || "—")}
                    </div>
                ) : (
                    <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        Starting pitchers are not listed, or are hidden on your
                        SportsData plan (tier often shows “Scrambled” instead of
                        names).
                    </Paragraph>
                )}
                {row.away_hits != null || row.home_hits != null ? (
                    <div>
                        <Text type="secondary">Team hits (summary) </Text>
                        {(row.away_hits ?? "—") + "–" + (row.home_hits ?? "—")}
                    </div>
                ) : null}
                {loading ? (
                    <Spin size="small" />
                ) : err ? (
                    <Alert
                        type="warning"
                        showIcon
                        message="Box score unavailable"
                        description="The box score feed could not be loaded. Your API tier must include Box Score (Live & Final), and the game ID must be valid."
                    />
                ) : box ? (
                    <>
                        {box.tier_note ? (
                            <Alert type="info" showIcon message={box.tier_note} />
                        ) : null}
                        {decBits.length ? (
                            <div>
                                <Text type="secondary">Pitching decisions </Text>
                                {decBits.join(" · ")}
                            </div>
                        ) : null}
                        <LineScoreTable box={box} row={row} />
                        <BattingMiniTable
                            title={`${box.away_team || row.away_team} — batting`}
                            players={box.batting?.away}
                        />
                        <BattingMiniTable
                            title={`${box.home_team || row.home_team} — batting`}
                            players={box.batting?.home}
                        />
                    </>
                ) : null}
            </Space>
        </div>
    );
};

const LiveScoreStrip = ({ rows }) => {
    if (!rows || !rows.length) {
        return null;
    }
    return (
        <div className="wl-mlb-live-strip">
            {rows.map((row) => (
                <Card
                    key={row.game_id || row.key}
                    size="small"
                    className="wl-mlb-live-card"
                    title={
                        <Space wrap>
                            <Text strong>{row.label}</Text>
                            {Array.isArray(row.owners) &&
                                row.owners.map((o) => (
                                    <Tag key={o} color="blue">
                                        {o}
                                    </Tag>
                                ))}
                        </Space>
                    }
                >
                    <div className="wl-mlb-live-scoreline">
                        <span className="wl-mlb-live-teams">{row.away_team}</span>
                        <span className="wl-mlb-live-runs">
                            {row.away_runs != null && row.home_runs != null
                                ? `${row.away_runs}–${row.home_runs}`
                                : "—"}
                        </span>
                        <span className="wl-mlb-live-teams">{row.home_team}</span>
                    </div>
                    <div className="wl-mlb-live-meta">
                        {row.inning_description || inningLabel(row)}
                    </div>
                    {formatCount(row) ? (
                        <div className="wl-mlb-live-count">{formatCount(row)}</div>
                    ) : null}
                    <div className="wl-mlb-live-bases">{formatRunners(row)}</div>
                    {row.channel ? (
                        <div className="wl-mlb-live-tv">
                            <Text type="secondary">TV </Text>
                            {row.channel}
                        </div>
                    ) : null}
                    {row.away_starter || row.home_starter ? (
                        <div className="wl-mlb-live-sp">
                            <Text type="secondary">SP </Text>
                            {(row.away_starter || "—") +
                                " vs " +
                                (row.home_starter || "—")}
                        </div>
                    ) : null}
                    {row.last_play ? (
                        <Paragraph type="secondary" className="wl-mlb-last-play">
                            {row.last_play}
                        </Paragraph>
                    ) : null}
                </Card>
            ))}
        </div>
    );
};

const GameTable = ({ rows, props, showScore, showInning, recap }) => {
    const cols = [
        {
            title: props.isMobile() ? "When" : "Start",
            dataIndex: "start_iso",
            key: "start_iso",
            width: showInning ? 120 : 160,
            render: (iso, row) =>
                row.start_display && String(row.start_display).trim()
                    ? row.start_display
                    : formatStart(iso),
        },
        {
            title: "Matchup",
            dataIndex: "label",
            key: "label",
            render: (text, row) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{row.label}</Text>
                    <Text type="secondary" className="wl-mlb-game-status">
                        {row.status}
                    </Text>
                </Space>
            ),
        },
    ];
    if (showScore) {
        cols.push({
            title: "Score",
            key: "score",
            align: "center",
            width: 72,
            render: (_, row) =>
                row.away_runs != null && row.home_runs != null
                    ? `${row.away_runs}–${row.home_runs}`
                    : "—",
        });
    }
    if (showInning) {
        cols.push({
            title: "Inn",
            key: "inn",
            width: 56,
            align: "center",
            render: (_, row) =>
                row.inning_description || inningLabel(row),
        });
    }
    if (showInning) {
        cols.push({
            title: "Count",
            key: "cnt",
            width: 110,
            align: "center",
            render: (_, row) => formatCount(row) || "—",
        });
        cols.push({
            title: "Runners",
            key: "run",
            width: 100,
            align: "left",
            render: (_, row) => formatRunners(row),
        });
    }
    cols.push({
        title: "TV",
        dataIndex: "channel",
        key: "channel",
        width: 72,
        ellipsis: true,
        render: (c) => c || "—",
    });
    cols.push({
        title: "Owners",
        dataIndex: "owners",
        key: "owners",
        render: (owners) =>
            Array.isArray(owners) && owners.length ? (
                <Space size={[4, 4]} wrap>
                    {owners.slice(0, 6).map((o) => (
                        <Tag key={o}>{o}</Tag>
                    ))}
                </Space>
            ) : (
                "—"
            ),
    });

    const data = (rows || []).map((row, i) => ({
        ...row,
        key: row.game_id ? `g-${row.game_id}` : `gr-${i}`,
    }));

    if (!data.length) {
        return <Empty description="Nothing in this window yet." />;
    }

    const expand = recap
        ? {
              expandedRowRender: (row) => <GameRecap row={row} />,
              rowExpandable: (row) =>
                  !!(
                      row.game_id &&
                      (row.channel ||
                          row.away_starter ||
                          row.home_starter ||
                          row.away_hits != null ||
                          row.home_hits != null ||
                          /final|complete|inprogress|in progress|delayed|review|warmup|challenge|suspended/i.test(
                              row.status || ""
                          ))
                  ),
          }
        : undefined;

    return (
        <Table
            size="small"
            columns={cols}
            dataSource={data}
            pagination={{ pageSize: 8, hideOnSinglePage: true }}
            rowKey="key"
            expandable={expand}
        />
    );
};

const ScheduleTab = (props) => {
    const { schedulePollActive, ...restProps } = props;
    const cfg = { ...MLB_BLOCK_DEFAULTS, ...useBlockConfig() };
    const [extras, setExtras] = useState(() => getExtras());

    useEffect(() => {
        if (!schedulePollActive) {
            return undefined;
        }
        let pollMs = 30000;
        if (
            typeof wl !== "undefined" &&
            wl.mlb_schedule_poll_ms != null
        ) {
            const n = parseInt(wl.mlb_schedule_poll_ms, 10);
            if (!Number.isNaN(n) && n >= 15000) {
                pollMs = n;
            }
        }
        const fetchSchedule = () => {
            if (document.hidden) {
                return;
            }
            if (typeof wl === "undefined" || !wl.root) {
                return;
            }
            const url = `${wl.root}wins-league/v1/mlb-schedule`;
            fetch(url, {
                credentials: "same-origin",
                cache: "no-store",
                headers: wl.nonce ? { "X-WP-Nonce": wl.nonce } : {},
            })
                .then((res) => (res.ok ? res.json() : null))
                .then((data) => {
                    if (!data) {
                        return;
                    }
                    setExtras((prev) => ({
                        ...prev,
                        recent: data.recent ?? prev.recent,
                        live: data.live ?? prev.live,
                        upcoming: data.upcoming ?? prev.upcoming,
                        meta: { ...prev.meta, ...(data.meta || {}) },
                    }));
                })
                .catch(() => {});
        };
        fetchSchedule();
        const id = setInterval(fetchSchedule, pollMs);
        return () => clearInterval(id);
    }, [schedulePollActive]);

    const x = extras;
    const live = x.live || [];
    const recent = x.recent || [];
    const upcoming = x.upcoming || [];
    const meta = x.meta || {};

    const defaultKeys = ["recent", "up"];

    return (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {meta.schedule_note ? (
                <Paragraph type="secondary" className="wl-mlb-meta">
                    {meta.schedule_note}
                </Paragraph>
            ) : null}
            {cfg.show_live_strip && live.length ? (
                <Card
                    size="small"
                    title={
                        <Space>
                            <Tag color="red">Live</Tag>
                            <span>Live scoreboard</span>
                        </Space>
                    }
                    className="wl-mlb-live-wrap"
                >
                    <LiveScoreStrip rows={live} />
                </Card>
            ) : null}
            <Collapse defaultActiveKey={defaultKeys}>
                <Panel key="recent" header={`Recent (${recent.length})`}>
                    <GameTable
                        rows={recent}
                        props={restProps}
                        showScore
                        showInning={false}
                        recap={cfg.show_recaps}
                    />
                </Panel>
                <Panel key="up" header={`Upcoming (${upcoming.length})`}>
                    <GameTable
                        rows={upcoming}
                        props={restProps}
                        showScore={false}
                        showInning={false}
                    />
                </Panel>
            </Collapse>
        </Space>
    );
};

const InsightsTab = (props) => {
    const x = getExtras();
    const injuries = x.injuries || [];
    const ideas = x.api_ideas || [];
    const meta = x.meta || {};

    const injCols = [
        {
            title: "Player",
            dataIndex: "player",
            key: "player",
        },
        {
            title: props.isMobile() ? "Tm" : "Team",
            dataIndex: "team",
            key: "team",
            width: 52,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
        },
        {
            title: props.isMobile() ? "?" : "Body",
            dataIndex: "body_part",
            key: "body_part",
        },
    ];

    const injData = injuries.map((row, i) => ({
        ...row,
        key: `inj-${i}-${row.player}`,
    }));

    return (
        <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Card size="small" title="Injuries (league teams)">
                {injData.length ? (
                    <Table
                        size="small"
                        columns={injCols}
                        dataSource={injData}
                        pagination={{ pageSize: 10, hideOnSinglePage: true }}
                        rowKey="key"
                        scroll={{ x: "max-content" }}
                    />
                ) : (
                    <Empty description="No injury rows returned (endpoint may need a higher SportsData tier, or there are no listed injuries)." />
                )}
            </Card>
            <Card size="small" title="What the API could add next">
                {meta.standings_note ? (
                    <Paragraph type="secondary" className="wl-mlb-meta">
                        {meta.standings_note}
                    </Paragraph>
                ) : null}
                <ul className="wl-mlb-ideas">
                    {ideas.map((line, idx) => (
                        <li key={idx}>{line}</li>
                    ))}
                </ul>
                <Paragraph type="secondary">
                    See{" "}
                    <a
                        href="https://sportsdata.io/developers/api-documentation/mlb"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        SportsData MLB documentation
                    </a>{" "}
                    and your subscription tier for available feeds.
                </Paragraph>
            </Card>
        </Space>
    );
};

const MLB_Wins_Main = (props) => {
    const cfg = { ...MLB_BLOCK_DEFAULTS, ...useBlockConfig() };

    const firstTabKey =
        (cfg.show_standings && "standings") ||
        (cfg.show_draft && "draft") ||
        (cfg.show_schedule && "schedule") ||
        (cfg.show_insights && "insights") ||
        "standings";

    const [schedulePollActive, setSchedulePollActive] = useState(
        () => firstTabKey === "schedule"
    );

    const columns = [
        {
            title: "Standings",
            dataIndex: "standings",
            key: "standings",
            align: "left",
            render: (text, record) => (
                <Space size="small">
                    <span>{record.name}</span>
                    <AchievementBadges
                        beerEnabled={record.beerAchievementEnabled}
                        beerAnimation={record.beerAchievementAnimation}
                        achievements={record.achievements}
                        name={record.name}
                    />
                </Space>
            ),
        },
        {
            title: props.isMobile() ? "W" : "Total Wins",
            dataIndex: "total_wins",
            key: "total_wins",
            align: "center",
        },
        {
            title: props.isMobile() ? "L" : "Total Losses",
            dataIndex: "total_losses",
            key: "total_losses",
            align: "center",
        },
        {
            title: props.isMobile() ? "%" : "Total Winning Percentage",
            dataIndex: "total_percentage",
            key: "total_percentage",
            align: "center",
        },
    ];

    const guys = Array.isArray(wl.main_info.guys) ? wl.main_info.guys : [];

    const data = guys.map((guy) => {
        return {
            key: guy.mlb_guy,
            name: guy.mlb_guy,
            beerAchievementEnabled: Boolean(guy.beer_achievement_enabled),
            beerAchievementAnimation: guy.beer_achievement_animation,
            achievements: guy.league_achievements,
            total_wins: guy.total_wins,
            total_losses: guy.total_losses,
            total_percentage: guy.pct,
            team_data: guy.team_standings_data,
        };
    });

    const draftSummary = Array.isArray(wl.main_info.draft_summary)
        ? wl.main_info.draft_summary
        : [];

    const draftData = draftSummary.map((row, idx) => {
        const teamKey = row.key;
        return {
            ...row,
            mlb_team_key: teamKey,
            key: `draft-${idx}-${row.pick}-${teamKey}-${row.owner}`,
        };
    });

    const tabsSpec = [];
    if (cfg.show_standings) {
        tabsSpec.push({
            key: "standings",
            tab: "Standings",
            content: (
                <Table
                    className="wl-mlb-standings-table"
                    size="middle"
                    columns={columns}
                    dataSource={data}
                    expandable={{
                        expandedRowRender: (record) => (
                            <Guy_Table team_data={record.team_data} />
                        ),
                    }}
                    pagination={false}
                    scroll={props.isMobile() ? { x: "max-content" } : undefined}
                />
            ),
        });
    }
    if (cfg.show_draft) {
        tabsSpec.push({
            key: "draft",
            tab: `Draft order${
                draftData.length ? ` (${draftData.length})` : ""
            }`,
            content: draftData.length ? (
                <div
                    className="wl-mlb-draft-card-stack"
                    aria-label="Draft order"
                >
                    {draftData.map((row) => {
                        const cardRecord = {
                            key: row.mlb_team_key || row.key,
                            city: row.city,
                            name: row.name,
                            logo: row.logo,
                            wins: row.wins,
                            losses: row.losses,
                            percentage: row.percentage,
                            last10_record: row.last10_record,
                            last10_run: row.last10_run,
                            last10_games: row.last10_games,
                            next_games: row.next_games,
                        };
                        const pick =
                            row.pick == null ? "—" : String(row.pick);
                        return (
                            <MlbTeamMobileCard
                                key={row.key}
                                record={cardRecord}
                                metaPrefix={`#${pick} · ${row.owner}`}
                            />
                        );
                    })}
                </div>
            ) : (
                <Empty description="No draft data." />
            ),
        });
    }
    if (cfg.show_schedule) {
        tabsSpec.push({
            key: "schedule",
            tab: "Schedule & scores",
            content: (
                <ScheduleTab
                    {...props}
                    schedulePollActive={schedulePollActive}
                />
            ),
        });
    }
    if (cfg.show_insights) {
        tabsSpec.push({
            key: "insights",
            tab: "Injuries & roadmap",
            content: <InsightsTab {...props} />,
        });
    }

    if (!tabsSpec.length) {
        return (
            <Alert
                type="info"
                showIcon
                message="Nothing to display"
                description="Enable at least one section in the block settings (sidebar) to show standings, draft, schedule, or injuries."
            />
        );
    }

    return (
        <Fragment>
            <Tabs
                defaultActiveKey={tabsSpec[0].key}
                className="wl-mlb-tabs"
                animated={{ inkBar: true, tabPane: false }}
                onChange={(key) => setSchedulePollActive(key === "schedule")}
            >
                {tabsSpec.map(({ key, tab, content }) => (
                    <TabPane tab={tab} key={key}>
                        {content}
                    </TabPane>
                ))}
            </Tabs>
        </Fragment>
    );
};

export default withGetScreen(MLB_Wins_Main);
